import axios from '@api';
import {
  transformFlow,
  transformTopic,
  transformCreateNarrativeResponse,
  transformNarrativeItemsData,
  transformDraftTopicsData,
  transformNarrativePrompts,
  transformNarrativeTopics,
  transformNarrativeType,
} from './utilities/helpers';

/**
 * read flow
 * @param {Object} options
 * @param  {Number} options.id
 * @param  {Number}  options.signal
 * @return {Promise}
 */
export const readFlow = async ({ id, signal }) => {
  return axios({
    method: 'get',
    signal,
    url: `/api/flows/${id}`,
    withCredentials: true,
  }).then(({ data: flow }) => transformFlow(flow));
};

/**
 * ready flow topic
 * @param {Object} options
 * @param  {Number} options.id
 * @param {AbortSignal} [options.signal] AbortController signal
 * @return {Promise}
 */
export const readTopic = async ({ id, signal }) => {
  return axios({
    method: 'get',
    signal,
    url: `/api/flows/flowtopics/${id}`,
    withCredentials: true,
  }).then(({ data: topic }) => transformTopic(topic));
};

/**
 * read draft topic visualizations
 * @param {Object} options
 * @param {Array<string>} options.componentIds
 * @param {AbortSignal} [options.signal]
 * @return {Promise<Object>}
 */
export const readDraftTopics = async ({ componentIds = [], signal } = {}) => {
  if (!Array.isArray(componentIds) || componentIds.length === 0) return {};

  const params = new URLSearchParams();
  componentIds.forEach((componentId) => {
    if (componentId) {
      params.append('component_id', componentId);
    }
  });

  const queryString = params.toString();
  const url = queryString
    ? `/api/flows/flowtopics/drafts/?${queryString}`
    : '/api/flows/flowtopics/drafts/';

  return axios({
    method: 'get',
    signal,
    url,
    withCredentials: true,
  }).then(({ data }) => transformDraftTopicsData(data));
};

/**
 * read narrative list V2
 * @param  {Number} options.folderId
 * @param  {String} options.order   "asc" || "desc" || undefined
 * @param  {Number} options.page
 * @param  {String} options.query
 * @param  {Number} options.signal  AbortController signal
 * @param  {Number} options.size
 * @param  {String} options.sortBy
 * @param  {Array}  options.tags    [is_shared]
 * @return {Promise}
 */
export const readNarrativeItems = ({ order, page, query, signal, size, sortBy, tags }) => {
  return axios({
    method: 'get',
    params: {
      order_by: sortBy ? order : undefined,
      page,
      query: query?.length > 0 ? query : undefined,
      size,
      sort_by: sortBy,
      tags: tags?.length > 0 ? tags?.join(',') : undefined,
    },
    signal,
    transformResponse: [(data) => transformNarrativeItemsData(JSON.parse(data))],
    url: '/v2/api/flows',
    withCredentials: true,
  });
};

/**
 * update narrative name
 * @param  {Number} options.id
 * @param  {String} options.name
 * @return {Promise}
 */
export const updateNarrativeName = ({ id, name }) => {
  return axios({
    data: { name },
    method: 'put',
    url: `/v2/api/flows/${id}/rename`,
    withCredentials: true,
  });
};

/**
 * reads flow share token for read only
 * @param  {Number}  options.id
 * @return {Promise}
 */
export const readNarrativeShareToken = ({ id }) => {
  return axios({
    method: 'put',
    transformResponse: [
      (data) => {
        const response = JSON.parse(data);

        return response?.publicToken;
      },
    ],
    url: `/api/flows/${id}/share`,
    withCredentials: true,
  });
};

/**
 * Update topic visibility
 * @param {Object} options
 * @param {Number} options.id
 * @param {boolean} options.isHidden
 * @return {Promise}
 */
export const updateTopicVisibility = async ({ id, isHidden }) => {
  return axios({
    data: { is_hidden: isHidden },
    method: 'put',
    url: `/v2/api/flows/flowtopics/${id}/hide`,
    withCredentials: true,
  });
};

/**
 * shares narrative with all users under a client
 * @param  {Number} options.clientId
 * @param  {Number} options.ids
 * @return {Promise}
 */
export const shareNarrativeWithClientGroup = ({ clientId, ids }) => {
  return axios({
    data: {
      flow_ids: ids,
      recipient_client_id: clientId,
    },
    method: 'post',
    url: '/v2/api/flows/client_share',
    withCredentials: true,
  });
};

/**
 * shares narrative with users
 * @param  {Array} options.ids
 * @param  {Boolean} options.skipEmail
 * @param  {Number} options.userIds
 * @return {Promise}
 */
export const shareNarrativeWithUsers = ({ ids, skipEmail = false, userIds }) => {
  return axios({
    data: {
      flow_ids: ids,
      recipient_ids: userIds,
      skip_email: skipEmail,
    },
    method: 'post',
    url: '/v2/api/flows/user_share',
    withCredentials: true,
  });
};

/**
 * submit feedback for a narrative
 * @param  {Number} options.feedbackId
 * @param  {Boolean} options.like
 * @param  {String} options.reason
 * @param  {String} options.reasonExplanation
 * @return {Promise}
 */
export const submitNarrativeFeedback = ({ feedbackId, like, reason, reasonExplanation }) => {
  return axios({
    data: {
      like,
      reason,
      reason_explanation: reason === 'other' ? reasonExplanation : null,
    },
    method: 'post',
    url: `/v2/api/flows/flowtopics/${feedbackId}/feedback`,
    withCredentials: true,
  });
};

/**
 * deletes narratives
 * @param {Array} options.ids
 * @return {Promise}
 */
export const deleteNarratives = ({ ids }) => {
  return axios({
    data: {
      flow_ids: ids,
    },
    method: 'delete',
    url: '/v2/api/flows',
    withCredentials: true,
  });
};

/**
 * An api the returns data about an individual narrative
 * @function
 * @param {Function} transformFunction - Function to transform the fetched data (e.g., transformNarrativeType or transformNarrativePrompts).
 * @returns {Function} - A function that takes an object with { narrativeId, type } and returns a Promise resolving to the transformed data.
 */
const readNarrativeTypeCustomFunction = (transformFunction) => {
  return ({ flowId, narrativeId }) => {
    return axios({
      method: 'get',
      params: { flow_id: flowId },
      url: `/api/narratives/${narrativeId}/`,
      withCredentials: true,
    }).then(({ data }) => transformFunction(data));
  };
};

/**
 * Reads and transforms All Information about a Narrative type from api
 *
 * @function
 * @param {String} narrativeId i.e. brand-defense
 * @param {Number} [flowId] i.e. 123
 * @returns {Promise<Object>} Promise resolving to the transformed Narrative data
 */
export const readNarrativeType = readNarrativeTypeCustomFunction(transformNarrativeType);

/**
 * Reads and transforms Narrative to just it's prompts for use in ItemAnswers component
 *
 * @function
 * @param {String} narrativeId i.e. brand-defense
 * @returns {Promise<Object>} Promise resolving to the transformed Narrative prompts data.
 */
export const readNarrativePrompts = readNarrativeTypeCustomFunction(transformNarrativePrompts);

/**
 * @param {Object} options
 * @param {String} options.id
 * @param {PromptAnswer[]} options.promptAnswers
 * @param {String} options.title
 * @return {Promise}
 */
export const createNarrative = async ({ id, promptAnswers, title }) => {
  return axios({
    data: {
      answers: promptAnswers,
      name: title,
    },
    method: 'post',
    url: `/api/narratives/${id}`,
    withCredentials: true,
  }).then(({ data }) => transformCreateNarrativeResponse(data));
};

/**
 * Get all narrative types
 * @return {Promise}
 */
export const readNarratives = () => {
  return axios({
    method: 'get',
    url: '/v2/api/narratives',
    withCredentials: true,
  }).then(({ data }) => data);
};

/**
 * Get all narrative topics
 * @return {Promise}
 */
export const readNarrativeTopics = ({ signal }) => {
  return axios({
    method: 'get',
    signal,
    url: '/v2/api/narratives/topics',
    withCredentials: true,
  }).then(({ data }) => transformNarrativeTopics(data));
};
